import React, { useCallback, useRef, useState, type ReactNode } from 'react';
import {
  MdAccountTree,
  MdAdd,
  MdArrowBack,
  MdArrowForward,
  MdCheck,
  MdInfoOutline,
  MdPalette,
  MdPrecisionManufacturing,
} from 'react-icons/md';
import { Button, Modal } from '@aibuild/ui';
import type { CreateProcessRequest } from '@api/processes';
import {
  CardBlurb,
  CardBulletItem,
  CardBullets,
  CardCTA,
  CardHead,
  CardPreview,
  CardTag,
  CardTitle,
  CardsGrid,
  FooterHint,
  FooterSpacer,
  NameInput,
  NameStep,
  NameStepLabel,
  OptionCard,
  Spinner,
} from './NewProcessDialog.styled';

export interface NewProcessDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (req: CreateProcessRequest) => Promise<void>;
  isCreating: boolean;
  dataTestId?: string;
}

interface OptionAccent {
  bg: string;
  fg: string;
  border: string;
}

interface StarterOption {
  id: string;
  templateId: string;
  tag: string;
  title: string;
  blurb: string;
  bullets: readonly string[];
  icon: ReactNode;
  accent: OptionAccent;
  preview: ReactNode;
}

const ACCENT_BLANK: OptionAccent = {
  bg: 'rgb(193 193 255 / 0.14)',
  fg: '#C1C1FF',
  border: 'rgb(193 193 255 / 0.28)',
};

const ACCENT_CREATIVE: OptionAccent = {
  bg: 'rgb(241 169 241 / 0.14)',
  fg: '#F1A9F1',
  border: 'rgb(241 169 241 / 0.28)',
};

const ACCENT_ENGINEERING: OptionAccent = {
  bg: 'rgb(140 216 140 / 0.14)',
  fg: '#8CD88C',
  border: 'rgb(140 216 140 / 0.28)',
};

/* SVG previews — ports of the prototype's per-card miniature graphs. They
   read as a glance-able shape: blank (input → output), creative (gen-pick
   then linear chain), engineering (chain → gate → branched outputs). */

const BlankPreview = (
  <svg viewBox="0 0 240 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
    <rect x="14" y="34" width="50" height="32" rx="6" fill="rgb(193 193 255 / 0.18)" stroke="rgb(193 193 255 / 0.45)" />
    <rect x="176" y="34" width="50" height="32" rx="6" fill="rgb(140 216 140 / 0.18)" stroke="rgb(140 216 140 / 0.45)" />
    <path d="M64 50 L176 50" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5" strokeDasharray="3 3" fill="none" />
    <text x="39" y="54" textAnchor="middle" fontSize="8" fill="#C1C1FF" fontFamily="ui-monospace, monospace">input</text>
    <text x="201" y="54" textAnchor="middle" fontSize="8" fill="#8CD88C" fontFamily="ui-monospace, monospace">output</text>
  </svg>
);

const CreativePreview = (
  <svg viewBox="0 0 240 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
    <rect x="6" y="34" width="34" height="32" rx="5" fill="rgb(193 193 255 / 0.18)" stroke="rgb(193 193 255 / 0.45)" />
    <rect x="54" y="14" width="46" height="72" rx="5" fill="rgb(241 169 241 / 0.14)" stroke="rgb(241 169 241 / 0.45)" />
    <circle cx="65" cy="30" r="3" fill="rgb(241 169 241 / 0.7)" />
    <circle cx="77" cy="30" r="3" fill="rgb(241 169 241 / 0.7)" />
    <circle cx="89" cy="30" r="3" fill="rgb(241 169 241 / 0.7)" />
    <circle cx="65" cy="42" r="3" fill="rgb(241 169 241 / 0.35)" />
    <circle cx="77" cy="42" r="3" fill="#F1A9F1" stroke="#fff" strokeWidth="1" />
    <circle cx="89" cy="42" r="3" fill="rgb(241 169 241 / 0.35)" />
    <rect x="114" y="34" width="34" height="32" rx="5" fill="rgb(255 223 158 / 0.18)" stroke="rgb(255 223 158 / 0.45)" />
    <rect x="162" y="34" width="34" height="32" rx="5" fill="rgb(255 223 158 / 0.18)" stroke="rgb(255 223 158 / 0.45)" />
    <rect x="210" y="34" width="24" height="32" rx="5" fill="rgb(140 216 140 / 0.18)" stroke="rgb(140 216 140 / 0.45)" />
    <path d="M40 50 L54 50 M100 50 L114 50 M148 50 L162 50 M196 50 L210 50" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.2" fill="none" />
  </svg>
);

const EngineeringPreview = (
  <svg viewBox="0 0 240 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
    <rect x="6" y="34" width="30" height="32" rx="5" fill="rgb(193 193 255 / 0.18)" stroke="rgb(193 193 255 / 0.45)" />
    <rect x="46" y="34" width="30" height="32" rx="5" fill="rgb(255 223 158 / 0.18)" stroke="rgb(255 223 158 / 0.45)" />
    <rect x="86" y="34" width="30" height="32" rx="5" fill="rgb(255 223 158 / 0.18)" stroke="rgb(255 223 158 / 0.45)" />
    <rect x="126" y="34" width="34" height="32" rx="5" fill="rgb(255 194 120 / 0.22)" stroke="rgb(255 194 120 / 0.55)" />
    <text x="143" y="54" textAnchor="middle" fontSize="8" fill="#FFC278" fontFamily="ui-monospace, monospace">gate</text>
    <rect x="170" y="10" width="28" height="28" rx="5" fill="rgb(255 223 158 / 0.18)" stroke="rgb(255 223 158 / 0.45)" />
    <rect x="170" y="62" width="28" height="28" rx="5" fill="rgb(255 223 158 / 0.18)" stroke="rgb(255 223 158 / 0.45)" />
    <rect x="208" y="10" width="26" height="28" rx="5" fill="rgb(140 216 140 / 0.18)" stroke="rgb(140 216 140 / 0.45)" />
    <rect x="208" y="62" width="26" height="28" rx="5" fill="rgb(140 216 140 / 0.18)" stroke="rgb(140 216 140 / 0.45)" />
    <path d="M36 50 L46 50 M76 50 L86 50 M116 50 L126 50" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.2" fill="none" />
    <path d="M160 44 L170 24" stroke="#8CD88C" strokeWidth="1.2" fill="none" />
    <path d="M160 56 L170 76" stroke="#FF8B83" strokeWidth="1.2" fill="none" />
    <path d="M198 24 L208 24 M198 76 L208 76" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.2" fill="none" />
  </svg>
);

/* The 3 starter options. Template ids match the mock fixtures (and the
   server-side template catalogue when wired up). */
const OPTIONS: readonly StarterOption[] = [
  {
    id: 'blank',
    templateId: 'blank',
    tag: 'Blank canvas',
    title: 'Start from scratch',
    blurb:
      'An empty graph with one Input and one Output. Wire up your own steps — use this when you know what you want to build.',
    bullets: ['1 Input · 1 Output', 'No steps pre-wired', 'Full creative freedom'],
    icon: <MdAdd />,
    accent: ACCENT_BLANK,
    preview: BlankPreview,
  },
  {
    id: 'creative',
    templateId: 'creative_exploration',
    tag: 'Template · Creative',
    title: 'Creative exploration',
    blurb:
      'Generate many, pick one, lift it into 3D, render it. The shape of image → model → asset. Good for concept work and design ideation.',
    bullets: [
      'Generation node (pick 1 of N)',
      'Linear task chain after pick',
      '5 nodes · no gates',
    ],
    icon: <MdPalette />,
    accent: ACCENT_CREATIVE,
    preview: CreativePreview,
  },
  {
    id: 'engineering',
    templateId: 'engineering_workflow',
    tag: 'Template · Engineering',
    title: 'Engineering workflow',
    blurb:
      'CAD cleanup, stress simulation, human review gate, then branches to drawings or rework. Shows how approvals and branches work in a real process.',
    bullets: [
      '1 Gate with approved / rejected branches',
      '4 tasks + 3 outputs',
      '9 nodes · ready to publish',
    ],
    icon: <MdPrecisionManufacturing />,
    accent: ACCENT_ENGINEERING,
    preview: EngineeringPreview,
  },
];

/**
 * "New process" dialog. Two-step flow: first pick a template card, then
 * name the process. The name field is pre-filled with the template title
 * and auto-focused so the user can just type and press Enter.
 */
const NewProcessDialog: React.FC<NewProcessDialogProps> = ({
  open,
  onClose,
  onCreate,
  isCreating,
  dataTestId = 'new-process-dialog',
}) => {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [picked, setPicked] = useState<StarterOption | null>(null);
  const [name, setName] = useState('');
  const [wasOpen, setWasOpen] = useState(open);
  const inputRef = useRef<HTMLInputElement>(null);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPendingId(null);
      setPicked(null);
      setName('');
    }
  }

  const handlePick = (option: StarterOption) => {
    if (isCreating || pendingId !== null) return;
    setPicked(option);
    setName(option.templateId === 'blank' ? 'Untitled process' : option.title);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  };

  const handleBack = useCallback(() => {
    if (isCreating) return;
    setPicked(null);
    setName('');
  }, [isCreating]);

  const handleCreate = useCallback(async () => {
    if (!picked || isCreating || pendingId !== null) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    setPendingId(picked.id);
    try {
      await onCreate({
        name: trimmed,
        templateId: picked.templateId,
      });
    } finally {
      setPendingId(null);
    }
  }, [picked, isCreating, pendingId, name, onCreate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCreate();
      }
    },
    [handleCreate],
  );

  const title = picked ? `Name your process` : 'New process';
  const subtitle = picked
    ? undefined
    : 'Start blank or from a template. You can always rewire everything later — templates just give you a shape to start from.';

  return (
    <Modal
      open={open}
      onClose={isCreating ? () => undefined : onClose}
      title={title}
      subtitle={subtitle}
      icon={<MdAccountTree />}
      size={picked ? 'sm' : 'xl'}
      dataTestId={dataTestId}
      hideCloseButton={isCreating}
      disableEscape={isCreating}
      disableBackdropClose={isCreating}
      footer={
        picked ? (
          <>
            <Button
              variant="text"
              onClick={handleBack}
              disabled={isCreating}
              dataTestId={`${dataTestId}__back`}
            >
              <MdArrowBack />
              Back
            </Button>
            <FooterSpacer />
            <Button
              variant="text"
              onClick={onClose}
              disabled={isCreating}
              dataTestId={`${dataTestId}__cancel`}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleCreate}
              disabled={isCreating || !name.trim()}
              dataTestId={`${dataTestId}__create`}
            >
              {isCreating ? (
                <>
                  <Spinner aria-hidden="true" />
                  Creating…
                </>
              ) : (
                'Create'
              )}
            </Button>
          </>
        ) : (
          <>
            <FooterHint data-testid={`${dataTestId}__hint`}>
              <MdInfoOutline aria-hidden="true" />
              Every new process opens as a Draft. Publish it when the graph is
              runnable and you want teammates to use it.
            </FooterHint>
            <Button
              variant="text"
              onClick={onClose}
              disabled={isCreating}
              dataTestId={`${dataTestId}__cancel`}
            >
              Cancel
            </Button>
          </>
        )
      }
    >
      {picked ? (
        <NameStep data-testid={`${dataTestId}__name-step`}>
          <NameStepLabel htmlFor="new-process-name">Process name</NameStepLabel>
          <NameInput
            ref={inputRef}
            id="new-process-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isCreating}
            placeholder="e.g. Bracket stress analysis"
            maxLength={120}
            autoComplete="off"
            data-testid={`${dataTestId}__name-input`}
          />
        </NameStep>
      ) : (
        <CardsGrid
          role="group"
          aria-label="Starter templates"
          data-testid={`${dataTestId}__options`}
        >
          {OPTIONS.map((o) => (
            <OptionCard
              key={o.id}
              type="button"
              onClick={() => handlePick(o)}
              disabled={isCreating}
              $accentBg={o.accent.bg}
              $accentFg={o.accent.fg}
              $accentBorder={o.accent.border}
              data-testid={`${dataTestId}__option-${o.id}`}
            >
              <CardPreview>{o.preview}</CardPreview>
              <CardHead>
                <CardTag
                  $accentBg={o.accent.bg}
                  $accentFg={o.accent.fg}
                  $accentBorder={o.accent.border}
                >
                  {o.icon}
                  {o.tag}
                </CardTag>
              </CardHead>
              <CardTitle>{o.title}</CardTitle>
              <CardBlurb>{o.blurb}</CardBlurb>
              <CardBullets>
                {o.bullets.map((b) => (
                  <CardBulletItem key={b} $accentFg={o.accent.fg}>
                    <MdCheck aria-hidden="true" />
                    {b}
                  </CardBulletItem>
                ))}
              </CardBullets>
              <CardCTA>
                <span>Use this</span>
                <MdArrowForward aria-hidden="true" />
              </CardCTA>
            </OptionCard>
          ))}
        </CardsGrid>
      )}
    </Modal>
  );
};

export default NewProcessDialog;
